/**
 * Session expiry — send the user to sign in when the server rejects their token.
 *
 * Every domain service builds its own fetch with the stored Bearer token, and
 * each one surfaced a 401 as a raw error ("DTO API Error: 401 Session expired
 * or invalid…") while the app still looked signed in. Since 2026-10-24
 * username/password tokens are signed and expire after 12 hours, and a token
 * issued by the previous deploy (or by another server process without a shared
 * AUTH_TOKEN_SECRET) is rejected — so this now happens in normal use.
 *
 * One wrapper around window.fetch handles it for every service: a 401 from our
 * own /api/ (other than the sign-in calls themselves) clears the stored session
 * and goes to /login, where a "please sign in again" note is shown.
 */

const NOTE_KEY = 'session-expired';
const SKIP = ['/api/auth/login', '/api/auth/verify'];

function hadBearer(init) {
  const h = init && init.headers;
  if (!h) return false;
  if (typeof h.get === 'function') return !!h.get('Authorization');
  return !!(h.Authorization || h.authorization);
}

export function installSessionExpiryHandler() {
  if (typeof window === 'undefined' || window.__sessionExpiryInstalled) return;
  window.__sessionExpiryInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await originalFetch(input, init);
    if (res.status !== 401) return res;

    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!url.includes('/api/') || SKIP.some(p => url.includes(p)) || !hadBearer(init)) return res;
    if (window.location.pathname === '/login') return res;

    try {
      sessionStorage.removeItem('oidc_token');
      sessionStorage.removeItem('oidc_user');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.setItem(NOTE_KEY, '1');
    } catch (_) { /* storage blocked — still redirect */ }
    window.location.assign('/login');
    return res;
  };
}

/** True once after a forced sign-out; clears itself. */
export function takeSessionExpiredNote() {
  try {
    const v = sessionStorage.getItem(NOTE_KEY) === '1';
    sessionStorage.removeItem(NOTE_KEY);
    return v;
  } catch (_) {
    return false;
  }
}
